
import Vue, { PropType } from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import moment from 'moment-timezone';
import {
	FeedbackReport,
	FeedbackReportBoard,
	FeedbackReportComment,
	FeedbackReportKind,
	FeedbackReportListRow,
	FeedbackReportOrigin,
	FeedbackReportOriginKind,
	FeedbackReportPriority,
	FeedbackReportStatus,
	FEEDBACK_REPORT_PRIORITIES,
	FEEDBACK_REPORT_STATUSES,
	feedbackReportStatus,
	User,
} from '@run-diffusion/shared';
import { db, DocumentReference, functions } from '@/firebase';
import { ROUTER } from '@/router/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import NumberField from '@/components/base/NumberField.vue';
import FeedbackAssignDialog from './FeedbackAssignDialog.vue';
import FeedbackChip from './FeedbackChip.vue';
import FeedbackActivity from './FeedbackActivity.vue';
import FeedbackComments from './FeedbackComments.vue';
import FeedbackShotViewer from './FeedbackShotViewer.vue';
import FeedbackStatusDialog from './FeedbackStatusDialog.vue';
import { feedbackFileUrl } from './feedbackFiles';
import { copyText } from './clipboard';
import { FeedbackAdmin } from './feedbackAdmins';
import {
	AgentReportLinks,
	allTags,
	arrival,
	askedDuringWork,
	cleanTag,
	copiedMessage,
	currentHolder,
	FeedbackFileLinksResult,
	hasBoard,
	hasBuild,
	hasEnv,
	hasViewport,
	heldByOther,
	httpLink,
	isAdminReport,
	isClosed,
	isPullRequestUrl,
	KIND_LABELS,
	KIND_QUESTIONS,
	listRowOf,
	ORIGIN_LABELS,
	originKind,
	PRIORITY_LABELS,
	ReportHolder,
	reportForAgent,
	STATUS_LABELS,
	statusNeedsNote,
	statusOf,
	tagsOf,
	takeoverOutcomeText,
} from './feedbackReports';
import * as writes from './feedbackWrites';

// how long Copy for agent waits for the server's comments before copying what it has
const COMMENTS_WAIT_MS: number = 5000;

// The one obvious next step for a report in each status. A person approves it for an agent; the
// agent takes it into a pull request; it's marked merged, then moved to QA test once it's live,
// then done. The status menu makes any other move.
interface NextStep {
	label: string
	to?: FeedbackReportStatus
	merged?: boolean
}
const NEXT_STEP: Record<FeedbackReportStatus, NextStep> = {
	new: { label: 'Ready for agent', to: 'ready_for_agent' },
	triaged: { label: 'Ready for agent', to: 'ready_for_agent' },
	needs_human: { label: 'Ready for agent', to: 'ready_for_agent' },
	ready_for_agent: { label: 'Start work', to: 'in_progress' },
	in_progress: { label: 'Move to In PR', to: 'in_pr' },
	in_pr: { label: 'Ready to test', to: 'qa_test' },
	qa_test: { label: 'Mark done', to: 'done' },
	done: { label: 'Reopen', to: 'triaged' },
	wontfix: { label: 'Reopen', to: 'triaged' },
	duplicate: { label: 'Reopen', to: 'triaged' },
};

/**
 * One feedback report on the Feedback admin page: what the tester sent (their words, screenshots,
 * the board's recent activity, their device), and its triage. A report a developer's agent filed,
 * imported from a list, or filed here has no board: it shows who filed it and what the list says
 * instead. Every change goes through restServer as the signed-in person (./feedbackWrites.ts),
 * naming the status the page showed, so a report someone else has just changed is refused, not
 * changed from a stale copy; the report's listener shows the change. "Working on it" claims the
 * report, lets it go, takes it over (naming the claim it replaces), or assigns it to another
 * system admin (FeedbackAssignDialog). "Copy for agent" puts the report on the clipboard as
 * Markdown, links included, for a developer's agent session.
 */
export default Vue.extend({
	name: 'FeedbackReportDetail',
	props: {
		report: { type: Object as PropType<FeedbackReport>, required: true },
		// every loaded report, for the duplicate picker and the tags in use
		reports: { type: Array as PropType<FeedbackReport[]>, default: () => [] },
	},
	data () {
		return {
			shotUrls: [] as string[],
			shotsLoading: true,
			// the screenshot open full size, or null
			viewing: null as number | null,
			// the status waiting on its note (needs_human, wontfix, duplicate, in_pr...), or null
			statusDialog: null as FeedbackReportStatus | null,
			// the status the report had when that dialog opened. The change names it, so the API
			// refuses it if someone else has changed the status since, and the dialog says so.
			statusDialogFrom: null as FeedbackReportStatus | null,
			// remounts the status menu, so one whose change was cancelled shows the saved status
			statusResets: 0,
			comments: [] as FeedbackReportComment[],
			commentsLoading: true,
			commentsError: false,
			// settles once the comments have loaded from the server, or failed to: Copy for agent waits
			// for it. The first snapshot can come from the local cache, and miss what changed since.
			commentsSettled: null as Promise<void> | null,
			commentsFromCache: true,
			names: {} as Record<string, string>,
			saving: null as string | null,
			copying: false,
			prDraft: '',
			duplicateDraft: null as number | null,
			tagSearch: null as string | null,
			// what taking the report over from whoever holds it leads to, while the confirmation is open:
			// claiming it, claiming it and starting work on it, or assigning it to `assignee`
			takeover: null as 'claim' | 'progress' | 'assign' | null,
			// who held it as the confirmation opened: confirming takes it from them, and nobody else
			takeoverFrom: null as ReportHolder | null,
			// the admin picker is open
			assigning: false,
			// the admin the report is being assigned to, while the take-over confirmation is about them
			assignee: null as FeedbackAdmin | null,
			unsubscribe: null as (() => void) | null,
			kindLabels: KIND_LABELS,
			originLabels: ORIGIN_LABELS,
			statusLabels: STATUS_LABELS,
			priorityLabels: PRIORITY_LABELS,
			statusItems: FEEDBACK_REPORT_STATUSES.map((value: FeedbackReportStatus) => ({ text: STATUS_LABELS[value], value })),
			priorityItems: [
				{ text: 'No priority', value: null },
				...FEEDBACK_REPORT_PRIORITIES.map((value: FeedbackReportPriority) => ({ text: PRIORITY_LABELS[value], value })),
			],
			kindItems: Object.keys(KIND_LABELS).map((value: string) => ({ text: KIND_LABELS[value], value })),
		};
	},
	computed: {
		...mapState([
			'user',
			'authUser',
		]),
		...mapGetters([
			'displayLabels',
		]),
		// its status in today's ids (one written before ADR 0042 reads as the status it became)
		status (): FeedbackReportStatus {
			return statusOf(this.report);
		},
		closed (): boolean {
			return isClosed(this.report);
		},
		isAdmin (): boolean {
			return isAdminReport(this.report);
		},
		mergedAt (): Date | null {
			return this.report.triage?.mergedAt ? this.report.triage.mergedAt.toDate() : null;
		},
		tags (): string[] {
			return tagsOf(this.report);
		},
		// every tag in use on the loaded reports, to pick from
		tagItems (): string[] {
			return allTags(this.reports);
		},
		// The note left with the change into the current status (the question, the reason), if any.
		statusNote (): FeedbackReportComment | null {
			for (let i = this.comments.length - 1; i >= 0; i--) {
				const comment: FeedbackReportComment = this.comments[i];
				if (comment.kind !== 'status') continue;
				return feedbackReportStatus(comment.to) === this.status && comment.body ? comment : null;
			}
			return null;
		},
		statusNoteTitle (): string {
			if (this.status === 'needs_human') return 'Waiting on a person';
			if (this.status === 'wontfix') return 'Closed as won\'t fix';
			if (this.status === 'duplicate') return `Closed as a duplicate of #${this.report.triage?.duplicateOf ?? '?'}`;
			if (this.status === 'qa_test') return 'Ready to test';
			if (this.status === 'in_pr') return 'In a pull request';
			return `Moved to ${STATUS_LABELS[this.status]}`;
		},
		statusNoteIcon (): string {
			if (this.status === 'needs_human') return 'mdi-account-question-outline';
			if (this.status === 'duplicate') return 'mdi-content-copy';
			if (this.status === 'in_pr') return 'mdi-source-pull';
			if (this.status === 'qa_test') return 'mdi-test-tube';
			return 'mdi-information-outline';
		},
		questions (): { happened: string, expected: string } {
			return KIND_QUESTIONS[this.report.kind];
		},
		priority (): FeedbackReportPriority | null {
			return this.report.triage?.priority || null;
		},
		nextStep (): NextStep {
			// a question asked while it was approved work goes back to work once answered
			if (askedDuringWork(this.report, this.comments)) return { label: 'Back to agent work', to: 'ready_for_agent' };
			// a fix in a pull request is marked merged before it's live
			if (this.status === 'in_pr' && this.report.triage?.pr && !this.mergedAt) return { label: 'Mark merged', merged: true };
			return NEXT_STEP[this.status];
		},
		// Who is working on it: a person, or a developer's agent
		holder (): ReportHolder | null {
			return currentHolder(this.report);
		},
		// This person holds it themself (not their agent)
		isMine (): boolean {
			return !!this.holder && !!this.user && this.holder.uid === this.user.id && this.holder.kind === 'human';
		},
		holderName (): string {
			if (!this.holder) return '';
			if (this.isMine) return 'You';
			if (this.holder.kind === 'agent' && this.user && this.holder.uid === this.user.id) {
				return this.$vuetify.breakpoint.smAndUp ? `Your agent (${this.holder.agent || 'Agent'})` : `Your ${this.holder.agent || 'agent'}`;
			}
			return this.holder.name || this.nameOf(this.holder.uid);
		},
		// Who assigned it to its holder, when somebody else did
		assignedByName (): string {
			return this.holder?.assignedBy ? this.nameOf(this.holder.assignedBy) : '';
		},
		holderTitle (): string {
			if (!this.holder) return '';
			const name: string = this.holder.name || this.holderName;
			return this.holder.assignedBy ? `${name}, assigned by ${this.assignedByName}` : name;
		},
		// Nobody holds a report past its pull request: in PR, QA test or closed
		claimable (): boolean {
			return !['in_pr', 'qa_test', 'done', 'wontfix', 'duplicate'].includes(this.status);
		},
		claimLabel (): string {
			if (!this.claimable) return '';
			if (!this.holder) return 'Claim it';
			return this.isMine ? 'Let it go' : 'Take over';
		},
		// Any admin assigns a report before its pull request to any system admin
		canAssign (): boolean {
			return this.claimable;
		},
		// Whoever the take-over confirmation is about
		takeoverHolder (): ReportHolder | null {
			return this.takeover ? this.takeoverFrom : null;
		},
		takeoverName (): string {
			return this.takeoverHolder ? this.takeoverHolder.name || this.nameOf(this.takeoverHolder.uid) : '';
		},
		takeoverTitle (): string {
			if (this.takeover === 'assign' && this.assignee) return `Assign #${this.report.number} to ${this.assignee.name}?`;
			return `Take over #${this.report.number}?`;
		},
		// What confirming does, to the holder the confirmation names: an agent whose own developer
		// becomes the holder keeps working on it
		takeoverOutcome (): string {
			if (!this.takeover || !this.takeoverHolder) return '';
			return takeoverOutcomeText(this.takeover, this.takeoverHolder, this.user.id, this.assignee);
		},
		// "4 minutes", "an hour"
		takeoverFor (): string {
			return this.takeoverHolder?.since ? moment(this.takeoverHolder.since).fromNow(true) : '';
		},
		sentAt (): Date | null {
			return this.report.createdAt ? this.report.createdAt.toDate() : null;
		},
		// "sent ... from the board's Feedback button", "filed ...", "imported ... from Boards v2 sheet"
		arrival (): { verb: string, via: string } {
			return arrival(this.report);
		},
		// filed by a developer's agent, imported from a list, or filed on this page; null for a
		// tester's report from a board
		origin (): FeedbackReportOriginKind | null {
			return originKind(this.report);
		},
		originTitle (): string {
			if (this.origin === 'import') return 'Imported from a list';
			if (this.origin === 'page') return 'Filed on this page';
			return 'Filed by an agent';
		},
		// what the list says of an imported report, and who filed it; empty for a board's report
		originFacts (): Partial<FeedbackReportOrigin> {
			return this.report.origin || {};
		},
		// the row of a list it's kept in step with (shown apart for one the list didn't file)
		listRow (): Partial<FeedbackReportListRow> | null {
			return listRowOf(this.report);
		},
		// the developer's agent, or the person, that filed it, as the page names them
		filedBy (): string {
			return this.report.origin?.name || 'A developer\'s agent';
		},
		originLink (): string | null {
			return httpLink(this.report.origin?.link);
		},
		// what it knows of its board, build and browser: one an agent filed or imported has an empty
		// placeholder, or null, for what it doesn't
		boardKnown (): boolean {
			return hasBoard(this.report.board);
		},
		buildKnown (): boolean {
			return hasBuild(this.report.build);
		},
		envKnown (): boolean {
			return hasEnv(this.report.env);
		},
		screenKnown (): boolean {
			return hasViewport(this.report.env);
		},
		// a report filed with no board, build or browser has no context to show
		hasContext (): boolean {
			return !this.origin || this.boardKnown || this.buildKnown || this.envKnown;
		},
		boardHref (): string {
			const board: FeedbackReportBoard | null = this.report.board;
			return hasBoard(board) ? this.$router.resolve({ name: ROUTER.BOARD, params: { boardId: board.id } }).href : '';
		},
		reportRef (): DocumentReference {
			return db.collection('feedbackReports').doc(this.report.id);
		},
		author (): { uid: string, name: string } {
			return { uid: this.user.id, name: this.authUser?.displayName || this.user.email || 'Admin' };
		},
		// The people Copy for agent names by their id (who assigned it): you by your name, others by email
		agentNames (): Record<string, string> {
			return { ...this.names, [this.user.id]: this.author.name };
		},
		prRule (): (value: string) => boolean | string {
			return (value: string): boolean | string => !(value || '').trim() || isPullRequestUrl(value) || 'A link that starts with https://';
		},
	},
	watch: {
		'report.triage.pr': {
			immediate: true,
			handler (pr: string | null) {
				this.prDraft = pr || '';
			},
		},
		'report.triage.duplicateOf': {
			immediate: true,
			handler (duplicateOf: number | null) {
				this.duplicateDraft = duplicateOf ?? null;
			},
		},
		'report.triage.owner': {
			immediate: true,
			handler (uid: string | null) {
				if (uid) this.loadName(uid);
			},
		},
		'report.triage.approvedBy': {
			immediate: true,
			handler (uid: string | null) {
				if (uid) this.loadName(uid);
			},
		},
		'report.claim.assignedBy': {
			immediate: true,
			handler (uid: string | null | undefined) {
				if (uid) this.loadName(uid);
			},
		},
	},
	created () {
		this.loadShots();
		let settle: () => void = () => {};
		this.commentsSettled = new Promise<void>((resolve) => { settle = resolve; });
		this.unsubscribe = this.reportRef.collection('comments')
			.orderBy('createdAt')
			.onSnapshot({ includeMetadataChanges: true }, (snap) => {
				// a comment just added has no server time yet: estimate it, so it sorts last and shows a time
				this.comments = snap.docs.map((doc) => ({ ...(doc.data({ serverTimestamps: 'estimate' }) as FeedbackReportComment), id: doc.id }));
				this.commentsLoading = false;
				this.commentsError = false;
				this.commentsFromCache = snap.metadata.fromCache;
				if (!snap.metadata.fromCache) settle();
			}, (e: Error) => {
				console.error('[feedback] comments failed to load', this.report.id, e);
				this.commentsLoading = false;
				this.commentsError = true;
				settle();
			});
	},
	beforeDestroy () {
		if (this.unsubscribe) this.unsubscribe();
		this.unsubscribe = null;
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		dateOf (timestamp: { toDate: () => Date } | null | undefined): Date | null {
			return timestamp ? timestamp.toDate() : null;
		},
		nameOf (uid: string): string {
			if (this.user && uid === this.user.id) return 'you';
			return this.names[uid] || uid;
		},
		async loadName (uid: string) {
			if (this.names[uid] || (this.user && uid === this.user.id)) return;
			try {
				const snap = await db.collection('users').doc(uid).get();
				if (snap.exists) this.$set(this.names, uid, (snap.data() as User).email || uid);
			} catch (e) {
				console.error('[feedback] a name failed to load', uid, e);
			}
		},
		async loadShots () {
			this.shotsLoading = true;
			this.shotUrls = await Promise.all(this.report.shots.map(async (shot) => {
				try {
					return await feedbackFileUrl(shot.path);
				} catch (e) {
					console.error('[feedback] a screenshot failed to load', shot.path, e);
					return '';
				}
			}));
			this.shotsLoading = false;
		},
		// One change through restServer: saving `key` meanwhile, the snackbar after. Resolves the
		// failure (null once saved), so a caller can act on a refusal's code.
		async save (key: string, change: () => Promise<writes.FeedbackApiResult<unknown>>, done: string): Promise<writes.FeedbackApiFailure | null> {
			this.saving = key;
			try {
				const failure = writes.feedbackApiFailure(await change());
				if (!failure) {
					this.updateSnackbar({ status: SNACKBAR_STATUS.SUCCESS, message: done, show: true });
					return null;
				}
				console.error('[feedback] failed to save', this.report.id, key, failure.code);
				// FEEDBACK_REPORT_MOVED: someone changed it since; the listener is showing it as it is now
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: failure.code === 'FEEDBACK_REPORT_MOVED'
						? `#${this.report.number} just changed, so nothing was saved. Check it and try again.`
						: failure.message,
					show: true,
				});
				return failure;
			} finally {
				this.saving = null;
			}
		},
		takeNextStep () {
			if (this.nextStep.merged) this.markMerged();
			else if (this.nextStep.to) this.setStatus(this.nextStep.to);
		},
		async setStatus (to: FeedbackReportStatus) {
			if (!to || to === this.status || this.saving) return;
			// starting work claims it: asking first when someone else holds it
			if (to === 'in_progress') {
				this.statusResets++;
				if (heldByOther(this.report, this.user.id)) this.openTakeover('progress');
				else await this.saveStatus('in_progress', this.status, '', null, null);
				return;
			}
			if (statusNeedsNote(to)) {
				this.statusDialogFrom = this.status;
				this.statusDialog = to;
				return;
			}
			if (!await this.saveStatus(to, this.status, '', null, null)) this.statusResets++;
		},
		// `from` is the status the change was decided on: the API refuses it if someone has changed
		// the report since.
		async saveStatus (to: FeedbackReportStatus, from: FeedbackReportStatus, note: string, duplicateOf: number | null, pullRequest: string | null, takeover: ReportHolder | null = null): Promise<boolean> {
			return !await this.moveTo(to, from, note, duplicateOf, pullRequest, takeover);
		},
		// The move itself: its failure, or null once saved. Starting work someone else has just
		// claimed asks to take it over from them.
		async moveTo (to: FeedbackReportStatus, from: FeedbackReportStatus, note: string, duplicateOf: number | null, pullRequest: string | null, takeover: ReportHolder | null = null): Promise<writes.FeedbackApiFailure | null> {
			if (to === from && to !== 'in_progress') return { ok: false, status: 0, code: 'NOTHING_TO_DO', message: '', details: null };
			const failure = await this.save('status', () => writes.moveReport(this.report.number, to, {
				note, expected: from, duplicateOf, pullRequest, takeoverOf: writes.takeoverOf(takeover),
			}), this.statusMessage(to, duplicateOf));
			if (failure) {
				if (failure.code === 'FEEDBACK_REPORT_CLAIMED' && to === 'in_progress') this.openTakeover('progress');
				return failure;
			}
			this.statusDialog = null;
			this.statusDialogFrom = null;
			this.$emit('status-changed', to);
			return null;
		},
		statusMessage (to: FeedbackReportStatus, duplicateOf: number | null): string {
			const n: string = `#${this.report.number}`;
			if (to === 'ready_for_agent') return `${n} is ready for an agent`;
			if (to === 'duplicate') return `${n} closed as a duplicate of #${duplicateOf}`;
			if (this.closed && !['done', 'wontfix', 'duplicate'].includes(to)) return `${n} reopened`;
			return `${n} is now ${STATUS_LABELS[to]}`;
		},
		closeStatusDialog () {
			this.statusDialog = null;
			this.statusDialogFrom = null;
			this.statusResets++;
		},
		onStatusConfirm ({ note, duplicateOf, pullRequest }: { note: string, duplicateOf: number | null, pullRequest: string | null }) {
			if (this.statusDialog && this.statusDialogFrom) this.saveStatus(this.statusDialog, this.statusDialogFrom, note, duplicateOf, pullRequest);
		},
		// The A key: approves a report for an agent. Returns whether it did.
		approve (): boolean {
			if (this.nextStep.to !== 'ready_for_agent' || this.saving) return false;
			this.setStatus('ready_for_agent');
			return true;
		},
		// The C key.
		focusComment () {
			const comments = this.$refs.comments as (Vue & { focus: () => void }) | undefined;
			if (comments) comments.focus();
		},
		setPriority (priority: FeedbackReportPriority | null) {
			if ((priority || null) === this.priority) return;
			this.save(
				'priority',
				() => writes.setPriority(this.report.number, priority || null),
				priority ? `#${this.report.number} is ${PRIORITY_LABELS[priority]}` : `#${this.report.number} has no priority`,
			);
		},
		setKind (kind: FeedbackReportKind) {
			if (!kind || kind === this.report.kind) return;
			this.save('kind', () => writes.setKind(this.report.number, kind), `#${this.report.number} is now ${KIND_LABELS[kind]}`);
		},
		// The tags field's new list: what it adds and removes, each checked as the API checks it
		async saveTags (wanted: string[]) {
			this.tagSearch = null;
			const cleaned: string[] = [];
			for (const raw of wanted || []) {
				const tag: string | null = cleanTag(raw);
				if (!tag) {
					this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: `"${raw}" isn't a tag: letters, digits, spaces and . _ : / + # & ' - only, up to 40`, show: true });
					return;
				}
				if (!cleaned.some((t: string) => t.toLowerCase() === tag.toLowerCase())) cleaned.push(tag);
			}
			const have: Set<string> = new Set(this.tags.map((t: string) => t.toLowerCase()));
			const keep: Set<string> = new Set(cleaned.map((t: string) => t.toLowerCase()));
			const add: string[] = cleaned.filter((t: string) => !have.has(t.toLowerCase()));
			const remove: string[] = this.tags.filter((t: string) => !keep.has(t.toLowerCase()));
			if (!add.length && !remove.length) return;
			await this.save('tags', () => writes.setTags(this.report.number, { add, remove }), add.length ? `Tagged #${this.report.number}` : `Tag removed from #${this.report.number}`);
		},
		markMerged () {
			if (this.saving) return;
			this.save('merged', () => writes.markMerged(this.report.number), `#${this.report.number}'s pull request is merged`);
		},
		// Claim it, let it go, or (after confirming) take it over from whoever holds it
		async claimAction () {
			if (this.saving) return;
			if (this.isMine) {
				await this.save('claim', () => writes.releaseReport(this.report.number, this.status), `You let go of #${this.report.number}`);
				return;
			}
			if (this.holder) {
				this.openTakeover('claim');
				return;
			}
			const failure = await this.save('claim', () => writes.claimReport(this.report.number, { expected: this.status }), `#${this.report.number} is yours`);
			if (failure?.code === 'FEEDBACK_REPORT_CLAIMED') this.openTakeover('claim');
		},
		// The confirmation is about whoever holds it as it opens: confirming takes it from them only
		// (and, assigning it, gives it to `assignee`)
		openTakeover (then: 'claim' | 'progress' | 'assign', assignee: FeedbackAdmin | null = null) {
			this.takeoverFrom = heldByOther(this.report, this.user.id);
			this.assignee = assignee;
			this.takeover = then;
		},
		async confirmTakeover () {
			const then: 'claim' | 'progress' | 'assign' | null = this.takeover;
			if (!then || this.saving) return;
			const from: ReportHolder | null = this.takeoverFrom;
			let failure: writes.FeedbackApiFailure | null;
			if (then === 'progress') {
				failure = await this.moveTo('in_progress', this.status, '', null, null, from);
				// claimed by someone else again meanwhile: the confirmation is already asking about them
				if (failure?.code === 'FEEDBACK_REPORT_CLAIMED') return;
			} else if (then === 'assign' && this.assignee) {
				const assignee: FeedbackAdmin = this.assignee;
				failure = await this.save('assign', () => writes.claimReport(this.report.number, { expected: this.status, takeoverOf: writes.takeoverOf(from), assignee: assignee.uid }), `Assigned #${this.report.number} to ${assignee.name}`);
			} else {
				failure = await this.save('claim', () => writes.claimReport(this.report.number, { expected: this.status, takeoverOf: writes.takeoverOf(from) }), `#${this.report.number} is yours`);
			}
			if (!failure) {
				this.takeover = null;
				this.assigning = false;
			} else if (failure.code === 'FEEDBACK_REPORT_MOVED' || failure.code === 'FEEDBACK_STATUS_CONFLICT') {
				// it changed hands, or closed, since the confirmation opened: it asks again about whoever has it now
				this.takeover = null;
			}
		},
		// The picker's choice: another admin is assigned it (confirming first over someone else's
		// claim); choosing yourself claims it.
		async assignTo (admin: FeedbackAdmin) {
			if (this.saving) return;
			if (admin.isMe) {
				this.assigning = false;
				if (!this.isMine) this.claimAction();
				return;
			}
			if (this.holder && this.holder.kind === 'human' && this.holder.uid === admin.uid) {
				this.assigning = false;
				this.updateSnackbar({ status: SNACKBAR_STATUS.INFO, message: `${admin.name} is already working on #${this.report.number}`, show: true });
				return;
			}
			if (heldByOther(this.report, this.user.id)) {
				this.assigning = false;
				this.openTakeover('assign', admin);
				return;
			}
			const failure = await this.save('assign', () => writes.claimReport(this.report.number, { expected: this.status, assignee: admin.uid }), `Assigned #${this.report.number} to ${admin.name}`);
			if (!failure) this.assigning = false;
			else if (failure.code === 'FEEDBACK_REPORT_CLAIMED') {
				this.assigning = false;
				this.openTakeover('assign', admin);
			}
		},
		// Esc in a field: back to the page's shortcuts (leaving it saves it, as clicking away does)
		leaveField (e: KeyboardEvent) {
			if (e.target instanceof HTMLElement) e.target.blur();
		},
		openPr () {
			if (this.report.triage?.pr) window.open(this.report.triage.pr, '_blank', 'noopener');
		},
		savePr () {
			const pr: string = (this.prDraft || '').trim();
			if ((pr || null) === (this.report.triage?.pr || null)) return;
			if (pr && !isPullRequestUrl(pr)) return;
			this.save(
				'pr',
				() => writes.setPullRequest(this.report.number, pr),
				pr ? `Pull request saved on #${this.report.number}` : `Pull request removed from #${this.report.number}`,
			);
		},
		// A duplicate names the report it repeats; the API checks it exists
		async saveDuplicateOf () {
			const n: number | null = Number.isInteger(this.duplicateDraft) && this.duplicateDraft > 0 && this.duplicateDraft !== this.report.number ? this.duplicateDraft : null;
			if (n === (this.report.triage?.duplicateOf ?? null)) return;
			if (n === null) {
				this.duplicateDraft = this.report.triage?.duplicateOf ?? null;
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: 'A duplicate needs the number of the report it repeats', show: true });
				return;
			}
			const failure = await this.save('duplicateOf', () => writes.moveReport(this.report.number, 'duplicate', { expected: 'duplicate', duplicateOf: n }), `#${this.report.number} repeats #${n}`);
			if (failure) this.duplicateDraft = this.report.triage?.duplicateOf ?? null;
		},
		async copyLink () {
			const href: string = this.$router.resolve({ name: ROUTER.ADMIN_FEEDBACK, params: { number: String(this.report.number) } }).href;
			try {
				await copyText(window.location.origin + href);
				this.updateSnackbar({ status: SNACKBAR_STATUS.SUCCESS, message: `Copied a link to #${this.report.number}`, show: true });
			} catch (e) {
				console.error('[feedback] failed to copy the link', e);
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: 'Couldn\'t copy the link, try again', show: true });
			}
		},
		// The report as Markdown, with links to its files that work for an hour (getFeedbackFileLinks),
		// once its comments have loaded. Without links, the files' storage paths stand in.
		async copyForAgent () {
			this.copying = true;
			try {
				// the comments as the server has them, or as they are after a few seconds' wait
				if (this.commentsSettled) await Promise.race([this.commentsSettled, new Promise((resolve) => window.setTimeout(resolve, COMMENTS_WAIT_MS))]);
				let links: AgentReportLinks = { shots: [], bundle: null, expiresAt: null };
				let linksMessage: string = '';
				try {
					const result = await functions.httpsCallable('getFeedbackFileLinks')({ reportId: this.report.id });
					const answer: FeedbackFileLinksResult = result.data as FeedbackFileLinksResult;
					if (answer.success === true) links = { shots: answer.shots, bundle: answer.bundle, expiresAt: answer.expiresAt };
					else linksMessage = answer.message;
				} catch (e) {
					console.error('[feedback] the links could not be made', this.report.id, e);
					linksMessage = 'The links could not be made.';
				}
				// what was copied: the comments can arrive while the clipboard is written
				const text: string = reportForAgent(this.report, this.comments, links, this.agentNames);
				const copied = copiedMessage(this.report.number, { links: linksMessage, commentsFailed: this.commentsError, commentsCached: this.commentsFromCache });
				await copyText(text);
				this.updateSnackbar({
					status: copied.ok ? SNACKBAR_STATUS.SUCCESS : (copied.warnOnly ? SNACKBAR_STATUS.WARN : SNACKBAR_STATUS.ERROR),
					message: copied.message,
					show: true,
				});
			} catch (e) {
				console.error('[feedback] failed to copy', this.report.id, e);
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: 'Couldn\'t copy the report, try again', show: true });
			} finally {
				this.copying = false;
			}
		},
	},
	components: {
		FeedbackActivity,
		FeedbackAssignDialog,
		FeedbackChip,
		FeedbackComments,
		FeedbackShotViewer,
		FeedbackStatusDialog,
		ConfirmDialog,
		NumberField,
	},
});
